import {useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import Button from '../../../../components/button';
import MainFooter from '../../../../components/mazraeKit/footers/footerMain';
import CustomHeader from '../../../../components/mazraeKit/headers/homeHeader';
import PageLoader from '../../../../components/pageLoader';
import useGetInstallmentPrice from '../../../../services/hookService/getInstallmentPrice/useGetInstallmentPrice';
import useGetTakenLoanDetails from '../../../../services/hookService/getTakenLoanDetails/useGetTakenLoanDetails';
import {getPersianDateTimeV2} from '../../../../utilities/helper/GetPersianDateTime';
import TabSelector from '../../../myAccount/my-orders/components/TabSelector';

type TabTypes = 'isPaid' | 'isNotPaid';

const LoanPaymentDetail = () => {
  const {isPaid, loanGuid} = useParams();
  const navigate = useNavigate();
  const {search} = useLocation();
  const searchParams = new URLSearchParams(search);
  const tabFromUrl = searchParams.get('tab') as TabTypes;

  const {data: installmentList, isLoading: isInstallmentListLoading} =
    useGetTakenLoanDetails(loanGuid);
  const {data: installmentPrice, isLoading: isInstallmentPriceLoading} =
    useGetInstallmentPrice(loanGuid);

  if (isPaid !== 'isPaid' && isPaid !== 'isNotPaid') {
    return <div>Invalid payment status</div>;
  }

  const tabsConfig =
    isPaid === 'isPaid'
      ? [{key: 'isNotPaid' as TabTypes, label: 'اقساط باقی‌مانده'}]
      : [
          {key: 'isPaid' as TabTypes, label: 'اقساط پرداخت شده'},
          {key: 'isNotPaid' as TabTypes, label: 'اقساط پرداخت نشده'},
        ];

  const isValidTab = tabsConfig.some(t => t.key === tabFromUrl);
  const [selectedTab, setSelectedTab] = useState<TabTypes>(
    isValidTab ? tabFromUrl! : isPaid === 'isPaid' ? 'isNotPaid' : 'isPaid',
  );

  useEffect(() => {
    const params = new URLSearchParams(search);
    params.set('tab', selectedTab);
    navigate({search: params.toString()}, {replace: true});
  }, [selectedTab, navigate, search]);

  // Filter payments based on selected tab
  const filteredPayments =
    installmentList?.installments?.filter(payment => {
      if (selectedTab === 'isPaid') {
        return payment.is_paid === true;
      } else {
        return payment.is_paid === false;
      }
    }) ?? [];

  const firstUnpaidInstallment = installmentList?.installments?.find(
    installment => !installment.is_paid,
  );

  const isLoading = isInstallmentPriceLoading || isInstallmentListLoading;

  if (isLoading) return <PageLoader />;

  return (
    <div className="flex flex-col w-full bg-white h-dvh">
      {/* ---------------- HEADER ---------------- */}
      <CustomHeader
        preComponent={() => (
          <div
            className="relative flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/my-credits')}
          >
            <i className="icon-nameArrow---Right text-[25px] text-gray-700" />
            <span className="font-medium">جزئیات پرداخت‌ها</span>
          </div>
        )}
      />

      {/* ---------------- CONTENT ---------------- */}
      <div className="flex flex-col h-full p-[1.5rem] overflow-y-auto">
        <TabSelector<TabTypes>
          gap="gap-4"
          justifyContent="justify-start"
          tabsConfig={tabsConfig}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />

        <div className="flex flex-col gap-4 mt-4 mb-8 last:pb-10">
          {filteredPayments?.map((payment, index) => {
            return (
              <LoanDetailField
                index={index}
                date={payment.due_date}
                price={payment.amount}
                key={payment.guid}
                finedPrice={payment.late_payment_penalty_amount}
                isPaid={payment.is_paid}
                selectedTab={selectedTab}
              />
            );
          })}
          <div className="flex items-center gap-1 pb-20 text-sm font-medium text-farm-shop-500">
            <i className="text-lg icon-calendar-tick" />
            <span>تسویه وام</span>
          </div>

          {filteredPayments?.length === 0 && (
            <div className="py-8 text-center text-gray-500">
              {selectedTab === 'isPaid'
                ? 'هیچ قسط پرداخت شده‌ای وجود ندارد'
                : 'هیچ قسط پرداخت نشده‌ای وجود ندارد'}
            </div>
          )}
        </div>
      </div>

      {selectedTab === 'isNotPaid' ? (
        <div className="fixed left-0 right-0 z-50 bottom-0 flex flex-col !bg-white shadow-[0_-1px_6px_0_rgba(0,0,0,0.1)]">
          {installmentList?.has_upcoming_installment &&
            firstUnpaidInstallment && (
              <div className="w-full px-6 py-2 text-sm text-right text-gray-700 bg-gray-50 ">
                سررسید پرداخت این قسط
                <span>
                  {' '}
                  {getPersianDateTimeV2(firstUnpaidInstallment?.due_date) ??
                    ''}{' '}
                </span>
                است
              </div>
            )}

          <div className="fixed left-0 right-0 z-50 bottom-0 !bg-white shadow-[0_-1px_6px_0_rgba(0,0,0,0.1)]">
            <div className="flex w-full gap-16 p-3">
              <div className="flex flex-col items-center justify-center gap-1 text-center">
                <span className="text-gray-700 text-md">مبلغ قابل پرداخت</span>
                <span className="font-medium text-gray-900 text-md">
                  {installmentPrice?.payment_price?.toLocaleString() + ' تومان'}
                </span>
              </div>
              <Button
                type="button"
                className="flex-1 w-full"
                disabled={!Boolean(installmentPrice?.payment_price)}
                onClick={() =>
                  navigate('/installment-payment-gateway', {
                    state: {loanGuid: loanGuid},
                  })
                }
              >
                پرداخت
              </Button>
            </div>

            <MainFooter isScrolling={false} />
          </div>
        </div>
      ) : (
        <MainFooter isScrolling={false} />
      )}
    </div>
  );
};

export default LoanPaymentDetail;

interface ILoanDetailField {
  index: number;
  date: string;
  price: number;
  finedPrice?: number;
  isPaid: boolean;
  selectedTab: string;
}

const LoanDetailField = ({
  index,
  date,
  price,
  finedPrice,
  selectedTab,
}: ILoanDetailField) => {
  return (
    <div className="py-[1rem] text-sm flex flex-col gap-2 relative font-normal border-b border-gray-100">
      <span className="text-gray-600">قسط {index + 1}</span>

      <div className="grid grid-cols-[auto_1fr] gap-x-6 items-center">
        <span className="text-gray-900 whitespace-nowrap w-[4rem]">
          {getPersianDateTimeV2(date)}
        </span>
        <span className="text-gray-900">
          {price.toLocaleString() + ' تومان'}
        </span>
      </div>

      {Boolean(finedPrice) && (
        <div className="grid grid-cols-[auto_1fr] gap-x-6 items-center">
          <span className="text-red-500 whitespace-nowrap w-[4rem]">
            جریمه دیرکرد
          </span>
          <span className="text-red-500">
            {finedPrice?.toLocaleString() + ' تومان'}
          </span>
        </div>
      )}

      {selectedTab === 'isNotPaid' && Boolean(finedPrice) && (
        <span className="absolute px-2 py-1 text-sm text-red-500 rounded-lg top-6 left-2 bg-red-50">
          عقب افتاده
        </span>
      )}
    </div>
  );
};
